import type Masonry from 'masonry-layout';

type MasonryImporter = () => Promise<{ default: typeof Masonry }>;

export class HelperInitializer {
	/** masonry-layout is code-split and only fetched on pages with a .masonry-container */
	constructor(private readonly masonryImporter: MasonryImporter = () => import('masonry-layout')) {}

	/**
	 * Initializes the Helper.
	 *
	 * @returns void
	 */
	init() {
		this.handleArrowButtons();
		this.masonryHandler();
		this.handleFooterMenu();
	}

	/**
	 * Creates an IntersectionObserver and calls the given callback function.
	 *
	 * @param callback The function that should be called when the observer is triggered.
	 *
	 * @returns IntersectionObserver
	 *
	 */
	static createObserver(callback: (element: Element) => void, repeat: boolean = false): IntersectionObserver {
		return new IntersectionObserver((entries, observer) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					callback(entry.target);
					if (repeat !== true) {
						observer.unobserve(entry.target);
					}
				}
			});
		});
	}

	masonryHandler() {
		const masonryElements = document.querySelectorAll('.masonry-container');
		if (!masonryElements.length) return;

		this.masonryImporter().then(({ default: MasonryLib }) => {
			masonryElements.forEach((masonryEl) => {
				const options: Masonry.Options = {
					// Exclude filtered-out cards: Masonry otherwise lays out hidden
					// (display:none) items, which measure 0x0 and corrupt the column
					// heights so visible cards overlap. reloadItems() re-applies this
					// selector on every filter change.
					itemSelector: '.masonry-item:not(.is-filtered-out)',
					percentPosition: true,
					horizontalOrder: true,
					// Reposition instantly. Animating positions while cards toggle
					// visibility leaves items stuck mid-transition, overlapping others.
					transitionDuration: '0s',
				};

				// A .grid-sizer keeps the column width stable when the first card is
				// filtered out (display:none); otherwise Masonry measures 0 and collapses
				// to a single column. A .gutter-sizer supplies the column gap, so gaps
				// stay correct regardless of which cards are filtered out.
				if (masonryEl.querySelector('.grid-sizer')) {
					options.columnWidth = '.grid-sizer';
				}
				if (masonryEl.querySelector('.gutter-sizer')) {
					options.gutter = '.gutter-sizer';
				}

				const masonry = new MasonryLib(masonryEl, options);

				(masonryEl as HTMLElement & { __masonry?: Masonry }).__masonry = masonry;

				const relayout = () => {
					if (typeof masonry.layout === 'function') masonry.layout();
				};
				relayout();

				// Card heights can still change after the first layout when web fonts
				// or images finish loading; relayout then so cards don't overlap.
				if (document.fonts?.ready) {
					document.fonts.ready.then(relayout);
				}
				window.addEventListener('load', relayout);
			});
		});
	}

	handleArrowButtons() {
		const arrowButtons = document.querySelectorAll<HTMLLinkElement>('.delo-button.arrow-button');

		arrowButtons.forEach((button) => {
			button.addEventListener('mouseenter', () => {
				// + 20 because of additional 10px padding on hover
				// + 5 because scrollWidth won't get the correct width
				const actualWidth = button.scrollWidth + 25;
				button.style.setProperty('--arrow-button-width', actualWidth + 'px');
			});

			button.addEventListener('mouseleave', () => {
				button.style.setProperty('--arrow-button-width', 'var(--arrow-button-height');
			});
		});
	}

	handleFooterMenu() {
		const footerMenuHeadlines = document.querySelectorAll<HTMLUListElement>(
			'.footer-nav-container .nav-folder > b'
		);

		footerMenuHeadlines.forEach((headline) => {
			headline.addEventListener('click', () => {
				const subMenu: HTMLLIElement | null = headline.nextElementSibling as HTMLLIElement | null;
				if (subMenu === null) return;

				subMenu.classList.toggle('open');
			});
		});
	}

	static lockScroll(): void {
		const scrollBarComp = window.innerWidth - document.documentElement.clientWidth;
		const scrollY = window.scrollY;

		// Save current scroll position
		document.body.dataset.scrollY = scrollY.toString();

		Object.assign(document.body.style, {
			position: 'fixed',
			top: `-${scrollY}px`,
			left: '0',
			width: '100%',
			overflow: 'hidden',
			paddingRight: `${scrollBarComp}px`,
		});
	}

	static unlockScroll(): void {
		const scrollY = parseInt(document.body.dataset.scrollY || '0', 10);

		// Reset styles
		Object.assign(document.body.style, {
			position: '',
			top: '',
			left: '',
			width: '',
			overflow: '',
			paddingRight: '',
		});

		// Restore scroll position
		window.scrollTo(0, scrollY);
	}
}
